import React, { useEffect } from "react";
import { Button, Col, Form, Input, Modal, Row, Select, Spin, Switch } from "antd";
import { ProductOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { fetchCategories } from "store/slices/categorySlice";
import type { MasterProduct, MasterProductPayload } from "../types";
import { generateProductCode } from "../utils";
import { UNIT_OPTIONS } from "shared/constants/units";

type FormFields = {
  name: string;
  category_id: number;
  default_unit: string;
  is_active: boolean;
};

export type MasterProductFormModalProps = {
  open: boolean;
  mode: "add" | "edit";
  initialProduct?: MasterProduct | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: MasterProductPayload) => void | Promise<void>;
};

const getInitialFields = (product?: MasterProduct | null): FormFields => ({
  name: product?.name || "",
  category_id: product?.category_id || 0,
  default_unit: product?.default_unit || "kg",
  is_active: product?.is_active ?? true,
});

export const MasterProductFormModal: React.FC<
  MasterProductFormModalProps
> = ({ open, mode, initialProduct, submitting = false, onClose, onSubmit }) => {
  const dispatch = useAppDispatch();
  const { categories, loading: categoriesLoading } = useAppSelector(
    (state) => state.categories
  );
  const [form] = Form.useForm<FormFields>();

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(getInitialFields(initialProduct));

    if (categories.length === 0 && !categoriesLoading) {
      dispatch(fetchCategories());
    }
  }, [open, initialProduct, form, categories.length, categoriesLoading, dispatch]);

  const watchedName = Form.useWatch("name", form);
  const productCode = generateProductCode(watchedName || "");

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  const submitForm = async () => {
    try {
      const values = await form.validateFields();

      await onSubmit({
        name: values.name.trim(),
        category_id: Number(values.category_id),
        default_unit: values.default_unit,
        is_active: values.is_active,
      });

      form.resetFields();
    } catch {
      return;
    }
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Master Product" : "Add Master Product"}
      open={open}
      onCancel={closeModal}
      footer={null}
      centered
      width={680}
      destroyOnClose
      className="add-mart-modal master-product-form-modal modal-round"
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Product Name"
              rules={[{ required: true, message: "Product name is required." }]}
            >
              <Input
                placeholder="Basmati Rice"
                size="large"
                className="input-round"
                prefix={<ProductOutlined style={{ color: "#35b558" }} />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Master Product Code"
              required
              extra="Auto-generated from product name."
            >
              <Input
                value={productCode}
                readOnly
                size="large"
                className="input-round master-product-code-input"
                placeholder="BASMATI-RICE"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="category_id"
              label="Category"
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("Category is required.")),
                },
              ]}
            >
              <Select
                size="large"
                showSearch
                optionFilterProp="label"
                placeholder="Select category"
                loading={categoriesLoading}
                options={categoryOptions}
                notFoundContent={
                  categoriesLoading ? (
                    <span className="package-select-state">
                      <Spin size="small" /> Loading categories...
                    </span>
                  ) : (
                    "No categories found."
                  )
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="default_unit"
              label="Default Unit"
              rules={[{ required: true, message: "Default unit is required." }]}
            >
              <Select size="large" options={UNIT_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="is_active" label="Status" valuePropName="checked">
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                className="package-status-switch"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className="mb-0 add-mart-modal-footer package-modal-footer">
          <Button onClick={closeModal} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            className="markets-add-btn btn-round"
            onClick={submitForm}
            loading={submitting}
          >
            Save Product
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MasterProductFormModal;
